import React, { useState, useEffect } from "react";
import { CheckCircle2, Circle, FileText, Download, ExternalLink, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { api, API as API_BASE } from "../../lib/api";

/**
 * /admin/cio-checklist — In-app, persistent step-by-step submission tracker
 * for the Charity Commission Association CIO application.
 *
 * Each step persists its done/notes state to localStorage so the trustees
 * can come back to it across sessions. Designed so the user can sit with
 * gov.uk open in one tab and this checklist open in another, ticking
 * sections off as they paste content into the form.
 */

const STORAGE_KEY = "abgn:cio-checklist:v1";

const STEPS = [
  {
    id: "1",
    title: "Pre-flight: governance pack ready",
    body: "Confirm every governance document is finalised. Check trustee names, charitable objects, three trustee declarations, and policies. Use the preview links below.",
    actions: [
      { kind: "pdf", label: "Governance Pack PDF", href: "/governance/pack.pdf" },
      { kind: "pdf", label: "Platform Fact Sheet", href: "/governance/platform-fact-sheet.pdf" },
      { kind: "pdf", label: "Cover Letter", href: "/governance/cover-letter.pdf" },
      { kind: "link", label: "Public Constitution", href: "/constitution", internal: true },
    ],
  },
  {
    id: "2",
    title: "Print & sign Trustee Declarations (×3)",
    body: "Section 11 of the Governance Pack contains the declarations. Print one for each of the 3 trustees. Each trustee signs, dates, and the documents are scanned ready for upload.",
    actions: [
      { kind: "pdf", label: "Open Governance Pack", href: "/governance/pack.pdf" },
    ],
  },
  {
    id: "3",
    title: "Send for friendly pre-review",
    body: "Before submitting to the Commission, send the pack to a mentor charity (e.g. Cranfield Trust), a charity-law solicitor, or a trustee at a similar charity. Wait 3-7 days for feedback.",
    actions: [
      { kind: "link", label: "Admin → Governance → Send", href: "/admin", internal: true },
    ],
  },
  {
    id: "4",
    title: "Decide initial annual income",
    body: "On the gov.uk form you'll need an estimated annual income. Under £5,000 lets you use simpler reporting. Under £25,000 still simpler. Pick a realistic 12-month figure.",
  },
  {
    id: "5",
    title: "Start the gov.uk application",
    body: "Visit the official Charity Commission registration form. Select CIO → Association model. Have the Governance Pack open beside you.",
    actions: [
      { kind: "external", label: "gov.uk/setting-up-charity/register-your-charity", href: "https://www.gov.uk/setting-up-charity/register-your-charity" },
    ],
  },
  {
    id: "6",
    title: "Paste in: charitable objects",
    body: "Copy Clause 3 verbatim from the Constitution page. Three objects: advancement of Buddhist religion, advancement of education, promotion of equality & inclusion.",
    actions: [
      { kind: "link", label: "Constitution (Clause 3)", href: "/constitution", internal: true },
    ],
  },
  {
    id: "7",
    title: "Paste in: public benefit statement",
    body: "Use the wording from /public-benefit, including the 'platform-based charitable delivery' section.",
    actions: [
      { kind: "link", label: "Public Benefit page", href: "/public-benefit", internal: true },
    ],
  },
  {
    id: "8",
    title: "Paste in: activities / how you deliver the objects",
    body: "Use the 12 platform activities + in-person events listed on the home page, plus the 4 pillars (Educate · Meditate · Empower · Elevate).",
  },
  {
    id: "9",
    title: "Enter trustee details (×3)",
    body: "Full legal name, date of birth, home address, email for each of: Mr Prafulla Ramteke (Chair), Miss Siddhi Dhiwar (Secretary), Mr Vishwas Dhiwar (Trustee). Only the Commission sees this — it is not public.",
  },
  {
    id: "10",
    title: "Upload constitution & declarations",
    body: "Attach the Governance Pack PDF and the three signed Trustee Declarations. If the form asks for 'anything else we should know', also attach the Platform Fact Sheet.",
    actions: [
      { kind: "pdf", label: "Bundle Zip (all 3 docs)", href: "/governance/bundle.zip" },
    ],
  },
  {
    id: "11",
    title: "Review, submit, get reference number",
    body: "Triple-check, submit. The form will display a reference number — paste it into Admin → Governance → Case reference. Save the confirmation email.",
    actions: [
      { kind: "link", label: "Admin → Governance", href: "/admin", internal: true },
    ],
  },
  {
    id: "12",
    title: "Update status in our system",
    body: "Open Admin → Governance, set status to 'Submitted to Commission', save. The /our-journey timeline updates publicly.",
    actions: [
      { kind: "link", label: "Set status to Submitted", href: "/admin", internal: true },
    ],
  },
  {
    id: "13",
    title: "Wait 30-45 working days for caseworker",
    body: "The Commission assigns a caseworker. When their email arrives, paste their address into Admin → Governance → Send and email them the bundle directly with a personalised cover letter.",
    actions: [
      { kind: "link", label: "Admin → Governance → Send", href: "/admin", internal: true },
    ],
  },
  {
    id: "14",
    title: "Reply to caseworker queries within 14 days",
    body: "Common queries are pre-empted in the pack. If anything new comes up, ping E1 (your AI dev) and we'll generate the supplementary document.",
  },
  {
    id: "15",
    title: "Receive charity number ✨",
    body: "On approval, the Commission gives you a charity number. Paste it into Admin → Governance, set status to 'Registered', click Save. The whole site updates instantly.",
    actions: [
      { kind: "link", label: "Admin → Governance", href: "/admin", internal: true },
    ],
  },
];

function loadState() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch (_e) {
    return {};
  }
}

function saveState(s) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch (_e) { /* ignore */ }
}

export default function CioChecklist() {
  const [state, setState] = useState(loadState);
  const [charityStatus, setCharityStatus] = useState(null);

  useEffect(() => { saveState(state); }, [state]);

  useEffect(() => {
    api.get("/charity/status").then((r) => setCharityStatus(r.data)).catch(() => {});
  }, []);

  const toggle = (id) => setState((s) => ({ ...s, [id]: { ...(s[id] || {}), done: !s[id]?.done } }));
  const setNote = (id, note) => setState((s) => ({ ...s, [id]: { ...(s[id] || {}), note } }));

  const completedCount = STEPS.filter((s) => state[s.id]?.done).length;
  const pct = Math.round((completedCount / STEPS.length) * 100);

  return (
    <div className="bg-pearl min-h-screen">
      <section className="bg-navy text-pearl py-14">
        <div className="section-container">
          <p className="eyebrow text-gold">Admin · Phase 3</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl">CIO Application Checklist</h1>
          <p className="mt-4 text-pearl/80 max-w-2xl">
            A 15-step interactive playbook for submitting our Charity Commission
            application. Tick steps as you complete them — your progress is
            saved locally in your browser.
          </p>
          {charityStatus && (
            <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-pearl/10 border border-saffron/40 rounded-full text-xs">
              <AlertCircle size={13} className="text-saffron" />
              <span>Current application status: <strong className="text-saffron uppercase tracking-wider">{charityStatus.status.replace(/_/g, " ")}</strong></span>
            </div>
          )}
          <div className="mt-6 max-w-md" data-testid="checklist-progress">
            <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-pearl/60">
              <span>Your progress</span>
              <span className="text-saffron">{completedCount} / {STEPS.length} done</span>
            </div>
            <div className="mt-2 h-1.5 bg-pearl/10 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-saffron to-gold transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="section-container max-w-3xl space-y-4">
          {STEPS.map((step) => {
            const done = !!state[step.id]?.done;
            const note = state[step.id]?.note || "";
            return (
              <article
                key={step.id}
                className={`bg-white border rounded-sm p-5 sm:p-6 transition-all ${
                  done ? "border-green-200 bg-green-50/40" : "border-navy/10"
                }`}
                data-testid={`step-${step.id}`}
              >
                <div className="flex items-start gap-4">
                  <button
                    type="button"
                    onClick={() => toggle(step.id)}
                    data-testid={`step-toggle-${step.id}`}
                    className={`mt-0.5 shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors ${
                      done ? "bg-green-600 text-white" : "bg-pearl text-slate-400 border-2 border-slate-300 hover:border-saffron"
                    }`}
                  >
                    {done ? <CheckCircle2 size={16} /> : <Circle size={14} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3">
                      <span className="font-mono text-xs text-saffron font-semibold">STEP {step.id}</span>
                      <h2 className={`font-display text-lg ${done ? "text-green-800 line-through" : "text-navy"}`}>
                        {step.title}
                      </h2>
                    </div>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{step.body}</p>
                    {step.actions && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {step.actions.map((a, i) => {
                          if (a.kind === "external") {
                            return (
                              <a
                                key={i}
                                href={a.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-saffron text-navy rounded-sm text-xs font-medium hover:bg-saffron-light"
                              >
                                <ExternalLink size={11} /> {a.label}
                              </a>
                            );
                          }
                          if (a.kind === "internal" || a.kind === "link") {
                            return (
                              <Link
                                key={i}
                                to={a.href}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-navy/15 text-navy rounded-sm text-xs hover:bg-navy/5"
                              >
                                <FileText size={11} /> {a.label}
                              </Link>
                            );
                          }
                          return (
                            <a
                              key={i}
                              href={`${API_BASE}${a.href}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-navy/15 text-navy rounded-sm text-xs hover:bg-navy/5"
                            >
                              <Download size={11} /> {a.label}
                            </a>
                          );
                        })}
                      </div>
                    )}
                    <textarea
                      value={note}
                      onChange={(e) => setNote(step.id, e.target.value)}
                      placeholder="Your notes for this step (date completed, caseworker ref, anything you want to remember…)"
                      rows={2}
                      data-testid={`step-note-${step.id}`}
                      className="mt-3 w-full text-xs border border-navy/10 px-3 py-2 rounded-sm focus:outline-none focus:border-gold"
                    />
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
